// ─────────────────────────────────────────────────────────
// layouts/AppLayout.tsx — C3 sidebar shell (icon rail + flyout)
//
// #sb = flex row: <IconRail 64px> + <NavFlyout 248px>
// Flyout is pinned (312px total) or unpinned (64px rail only,
// flyout slides in as a transient overlay on category click).
// ─────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
// useNavigate kept for topbar avatar → /profile
import { useAuthStore }      from '@/store/authStore'
import { useUIStore }        from '@/store/uiStore'
import Toast                 from '@/components/Toast'
import { OfflineBanner, SyncBar } from '@/components/OfflineBanner'
import { useOffline }        from '@/hooks/useOffline'
import { useCompanyData }    from '@/hooks/useCompanyData'
import CommandMenu           from '@/components/CommandMenu'
import ConnectionStatus      from '@/components/ConnectionStatus'
import { useConfirm }        from '@/components/ConfirmDialog'
import { doLogout }          from '@/services/auth'
import IconRail              from './nav/IconRail'
import NavFlyout             from './nav/NavFlyout'
import {
  NAV_CATEGORIES,
  ROUTE_TO_CATEGORY,
  countOverdueTasks,
  countDelayedMilestones,
  countPendingReceivables,
  countPendingPOs,
  countUnmatchedRecon,
  countPendingSalaries,
} from './nav/navData'
import { useDataStore }      from '@/store/dataStore'

// ── SyncErrorBanner ───────────────────────────────────────
function SyncErrorBanner() {
  const { syncError, clearSyncError } = useUIStore()
  if (!syncError) return null
  return (
    <div style={{
      position:   'fixed',
      top:        'var(--spacing-th)',
      left:       'var(--spacing-sw)',
      right:      0,
      zIndex:     150,
      background: '#FEF3C7',
      borderBottom: '1px solid #F59E0B',
      padding:    '7px 20px',
      display:    'flex',
      alignItems: 'center',
      gap:        10,
      fontSize:   12,
      color:      '#92400E',
      transition: 'left 200ms ease',
    }}>
      <svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0, color: '#D97706' }}>
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
      </svg>
      <span style={{ flex: 1 }}>{syncError}</span>
      <button
        onClick={clearSyncError}
        aria-label="Dismiss sync error"
        title="Dismiss"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#92400E', fontSize: 16, lineHeight: 1,
          padding: '0 2px', opacity: 0.7,
        }}
      >✕</button>
    </div>
  )
}

// ── AppLayout ─────────────────────────────────────────────
export default function AppLayout() {
  const { user }                         = useAuthStore()
  const { theme, toggleTheme, navFlyoutPinned, setNavFlyoutPinned } = useUIStore()
  const { task, milestones, recv, po, recon, sal } = useDataStore()
  const navigate    = useNavigate()
  const location    = useLocation()
  const [confirm, ConfirmDialog] = useConfirm()

  const [cmdOpen,     setCmdOpen]     = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // ── Mobile detection (JS-driven, bypasses all CSS specificity issues) ─
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 900)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // ── Current route segment ────────────────────────────────
  const seg = location.pathname.replace(/^\//, '').split('/')[0] ?? ''

  // ── selectedCategory: which category the flyout shows ────
  // Starts from the current route; updates when rail is clicked
  // or when the user navigates (browser back/forward, direct URL)
  const [selectedCategory, setSelectedCategory] = useState(() =>
    ROUTE_TO_CATEGORY[seg] ?? 'overview'
  )

  // Sync selectedCategory when route changes externally
  useEffect(() => {
    const cat = ROUTE_TO_CATEGORY[seg]
    if (cat) setSelectedCategory(cat)
  }, [seg])

  // ── Page title for topbar ────────────────────────────────
  const pageLabelMap: Record<string, string> = Object.fromEntries(
    NAV_CATEGORIES.flatMap(c => c.modules.map(m => [m.pg, m.label]))
  )
  const currentPageLabel = pageLabelMap[seg] ?? 'CivilBillDesk'

  // ── Badge counts for rail ─────────────────────────────────
  const categoryBadges = {
    overview:    countOverdueTasks(task) + countDelayedMilestones(milestones),
    tenders:     countPendingReceivables(recv),
    finance:     countUnmatchedRecon(recon),
    procurement: countPendingPOs(po),
    hr:          countPendingSalaries(sal),
    compliance:  0,
    settings:    0,
    'super-admin': 0,
  }

  // ── Track last-visited module per category ───────────────
  useEffect(() => {
    const cat = ROUTE_TO_CATEGORY[seg]
    if (!cat) return
    try {
      const stored = JSON.parse(localStorage.getItem('cbd_nav_last') ?? '{}') as Record<string, string>
      stored[cat] = seg
      localStorage.setItem('cbd_nav_last', JSON.stringify(stored))
    } catch { /* ignore */ }
  }, [seg])

  // ── nav-unpinned class on body (controls --spacing-sw) ────
  useEffect(() => {
    if (!navFlyoutPinned) {
      document.body.classList.add('nav-unpinned')
    } else {
      document.body.classList.remove('nav-unpinned')
    }
    return () => document.body.classList.remove('nav-unpinned')
  }, [navFlyoutPinned])

  // ── Global ⌘K / Ctrl+K ───────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCmdOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // ── Theme on mount ────────────────────────────────────────
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  useOffline()
  useCompanyData()

  // ── Notification permission (2s delay) ───────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      import('@/services/webNotifications')
        .then(m => m.requestNotificationPermission())
        .catch(() => {})
    }, 2_000)
    return () => clearTimeout(t)
  }, [])

  // ── Sign out ──────────────────────────────────────────────
  const handleSignOut = useCallback(() => {
    confirm({
      title:   'Sign out?',
      msg:     'Any unsaved changes will be lost.',
      okLabel: 'Sign Out',
      danger:  false,
      onOk:    doLogout,
    })
  }, [confirm])

  // ── Category selection from rail ──────────────────────────
  // Only swaps flyout content — does NOT navigate.
  // If flyout is closed, reopens it.
  const handleCategorySelect = useCallback((categoryId: string) => {
    setSelectedCategory(categoryId)
    if (!navFlyoutPinned) setNavFlyoutPinned(true)
  }, [navFlyoutPinned, setNavFlyoutPinned])

  // ── Active category data for flyout ──────────────────────
  const activeCategoryData = NAV_CATEGORIES.find(c => c.id === selectedCategory)
    ?? NAV_CATEGORIES[0]!

  // ── User initials ─────────────────────────────────────────
  const initials = (() => {
    const n = user?.displayName || user?.email || 'U'
    const parts = n.trim().split(' ')
    return parts.length > 1
      ? (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
      : n.slice(0, 2).toUpperCase()
  })()

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <OfflineBanner />
      <SyncBar />
      <SyncErrorBanner />

      {/* ── Desktop sidebar (rail + flyout) — hidden on mobile ─── */}
      {!isMobile && (
        <nav
          id="sb"
          aria-label="Main navigation"
          style={{
            display: 'flex', flexDirection: 'row',
            position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 200,
            background: 'var(--color-sb-bg)',
            borderRight: '1px solid var(--color-sb-bd)',
            width: 'var(--spacing-sw)',
            transition: 'width .2s ease',
            overflow: 'hidden',
          }}
        >
          <IconRail
            activeCategory={selectedCategory}
            onSelectCategory={handleCategorySelect}
            badges={categoryBadges}
            onSearch={() => setCmdOpen(true)}
            onSignOut={handleSignOut}
          />
          {navFlyoutPinned && (
            <NavFlyout
              category={activeCategoryData}
              activeRoute={seg}
              onClose={() => setNavFlyoutPinned(false)}
              onLinkClick={() => {}}
              badges={categoryBadges}
              companyId={user?.companyId ?? ''}
            />
          )}
        </nav>
      )}

      {/* ── Mobile drawer — separate portal-style overlay ─────────
          Rendered outside the flex-row layout so z-index is clean.
          Uses a portal-like approach: rendered into document.body  */}
      {isMobile && sidebarOpen && (
        <div style={{
          position: 'fixed', inset: 0,
          zIndex: 9999,           /* high z-index, above everything */
          display: 'flex',
        }}>
          {/* Dark backdrop */}
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.55)',
            }}
          />
          {/* Drawer panel */}
          <div style={{
            position: 'relative', zIndex: 1,
            width: 312, height: '100%',
            background: '#0D3B47',
            display: 'flex', flexDirection: 'row',
            overflow: 'hidden',
            boxShadow: '4px 0 24px rgba(0,0,0,0.5)',
            animation: 'slide-in .22s ease',
          }}>
            <IconRail
              activeCategory={selectedCategory}
              onSelectCategory={handleCategorySelect}
              badges={categoryBadges}
              onSearch={() => { setCmdOpen(true); setSidebarOpen(false) }}
              onSignOut={handleSignOut}
            />
            <NavFlyout
              category={activeCategoryData}
              activeRoute={seg}
              onClose={() => setSidebarOpen(false)}
              onLinkClick={() => setSidebarOpen(false)}
              badges={categoryBadges}
              companyId={user?.companyId ?? ''}
            />
          </div>
        </div>
      )}

      {/* ── Main area ──────────────────────────────────── */}
      <div id="main" role="main" style={{
        marginLeft: 'var(--spacing-sw)',
        flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh',
        transition: 'margin-left 200ms ease',
      }}>
        {/* Skip-to-content */}
        <a href="#main-content" className="skip-link">Skip to content</a>

        {/* Topbar */}
        <div id="top" style={{
          height: 'var(--spacing-th)',
          borderBottom: '1px solid var(--color-bd)',
          background: 'var(--color-bg)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          position: 'sticky', top: 0, zIndex: 100, flexShrink: 0,
        }}>
          <span id="ttl" style={{
            fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220,
            color: 'var(--color-tx)',
          }}>{currentPageLabel}</span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Hamburger — only rendered on mobile (JS-controlled, no CSS battle) */}
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(s => !s)}
                aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={sidebarOpen}
                aria-controls="sb"
                title="Toggle menu"
                style={{
                  width: 36, height: 36,
                  border: '1px solid var(--color-bd)',
                  borderRadius: 6,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--color-sf)',
                  color: 'var(--color-tx2)', cursor: 'pointer', flexShrink: 0,
                }}
              >
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M0 3.5A.5.5 0 0 1 .5 3h15a.5.5 0 0 1 0 1H.5a.5.5 0 0 1-.5-.5zm0 5A.5.5 0 0 1 .5 8h15a.5.5 0 0 1 0 1H.5a.5.5 0 0 1-.5-.5zm0 5a.5.5 0 0 1 .5-.5h15a.5.5 0 0 1 0 1H.5a.5.5 0 0 1-.5-.5z"/>
                </svg>
              </button>
            )}

            <ConnectionStatus />

            {/* ⌘K search trigger */}
            <button
              onClick={() => setCmdOpen(true)}
              aria-label="Open command menu"
              title="Command menu (⌘K)"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', border: '1px solid var(--color-bd)',
                borderRadius: 6, background: 'var(--color-sf)',
                color: 'var(--color-tx3)', cursor: 'pointer', fontSize: 11,
                transition: 'border-color .15s',
              }}
            >
              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="8.5" cy="8.5" r="5.5"/><path d="M15 15l-3.5-3.5"/>
              </svg>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                Search
                <kbd style={{ fontSize: 9, padding: '1px 4px', background: 'var(--color-bg2)', border: '1px solid var(--color-bd)', borderRadius: 3 }}>⌘K</kbd>
              </span>
            </button>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              title={theme === 'light' ? 'Dark mode' : 'Light mode'}
              style={{
                width: 30, height: 30, border: '1px solid var(--color-bd)',
                borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer',
                background: 'var(--color-sf)', color: 'var(--color-tx2)',
                transition: 'background .15s', flexShrink: 0,
              }}
            >
              <svg aria-hidden="true" width="15" height="15" viewBox="0 0 20 20" fill="currentColor">
                {theme === 'light'
                  ? <path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 2a6 6 0 010 12 6 6 0 01-4.24-1.76A5 5 0 0010 7a5 5 0 000-3z" clipRule="evenodd" fillRule="evenodd"/>
                  : <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 01-1.414 1.414l-.707-.707a1 1 0 011.414-1.414zm-7.07-1.414l.707.707A1 1 0 114.22 15.65l-.707-.707a1 1 0 011.414-1.414zm9.192-8.364l.707-.707a1 1 0 111.414 1.414l-.707.707a1 1 0 01-1.414-1.414zM4.22 4.22l.707.707a1 1 0 01-1.414 1.414L2.806 5.634a1 1 0 011.414-1.414zM17 9h1a1 1 0 110 2h-1a1 1 0 110-2zM2 9h1a1 1 0 110 2H2a1 1 0 110-2zm15.65 5.65l.707.707a1 1 0 11-1.414 1.414l-.707-.707a1 1 0 011.414-1.414zM4.22 15.78l.707-.707a1 1 0 111.414 1.414l-.707.707a1 1 0 01-1.414-1.414z"/>
                }
              </svg>
            </button>

            {/* User avatar (topbar shortcut) */}
            <button
              onClick={() => navigate('/profile')}
              aria-label={`Profile: ${user?.displayName ?? 'Account'}`}
              title="Your profile"
              style={{
                width: 26, height: 26,
                background: 'linear-gradient(135deg, #0898A8, var(--color-ac))',
                borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 8.5, fontWeight: 700,
                cursor: 'pointer', flexShrink: 0, letterSpacing: '.04em',
                border: 'none', padding: 0,
              }}
            >{initials}</button>
          </div>
        </div>

        {/* Page content */}
        <div id="main-content" tabIndex={-1} style={{ padding: '20px 24px', flex: 1 }}>
          <Outlet />
        </div>
      </div>

      <CommandMenu open={cmdOpen} onClose={() => setCmdOpen(false)} />
      <Toast />
      {ConfirmDialog}
    </div>
  )
}
